import React, { useState, useEffect, useCallback } from "react";
import { Field, FormikErrors, useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import {
  BaseExpenseFieldsProps,
  ExpenseEntry,
  ExpenseFormData,
  STAFF_CATEGORIES,
  StaffCategory,
} from "./types";
import { staffApi } from "../../../services/api";
import { formatInputCurrency } from "../../../utils/utils";
import {
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import { Visibility } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { Button, Checkbox, FormControlLabel } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

// Debounce hook for search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function DriverCleanerExpenseFields({
  index,
  isTripExpense = false,
}: BaseExpenseFieldsProps) {
  const { values, setFieldValue, errors } = useFormikContext<ExpenseFormData>();
  const [staffOptions, setStaffOptions] = useState<any[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);
  const expenseErrors = errors.expenses?.[index] as
    | FormikErrors<ExpenseEntry>
    | undefined;
  const [showStaffDetails, setShowStaffDetails] = useState<boolean>(false);
  const [addNotes, setAddNotes] = useState<boolean>(false);
  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Get current category
  const currentCategory =
    (values.expenses[index]?.category as StaffCategory) ||
    (values.expenses[index]?.type?.toUpperCase() as StaffCategory);

  // Search staff data based on category
  const searchStaffData = useCallback(
    async (term: string, category: StaffCategory) => {
      if (!category) return;
      if (term.length < 2 && term.length > 0) return;

      setIsLoading(true);
      setSearchError("");

      try {
        let data: any[] = [];
        switch (category) {
          case "DRIVER":
            data = await staffApi.searchDrivers(term);
            break;
          case "CLEANER":
            data = await staffApi.searchCleaners(term);
            break;
          default:
            data = [];
        }
        setStaffOptions(data);
      } catch (error) {
        setSearchError(`Failed to load ${category.toLowerCase()} data`);
        console.error(`Error searching ${category}:`, error);
        setStaffOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Effect to search when debounced term or category changes
  useEffect(() => {
    if (currentCategory) {
      searchStaffData(debouncedSearchTerm, currentCategory);
    } else {
      setStaffOptions([]);
    }
  }, [debouncedSearchTerm, currentCategory, searchStaffData]);

  // Initialize state from form values (for edit mode)
  useEffect(() => {
    const expense = values.expenses[index];
    if (expense) {
      // Initialize manual entry mode if referenceId is empty but paidTo is filled
      if (!expense.referenceId && expense.paidTo) {
        setIsManualEntry(true);
      }

      // Try to find and set selected staff if referenceId exists
      if (expense.referenceId && staffOptions.length > 0) {
        const staff = staffOptions.find((s) => s.id === expense.referenceId);
        if (staff) {
          setSelectedStaff(staff);
          setIsManualEntry(false);
        }
      }
    }
  }, [values.expenses, index, staffOptions]);

  // Handle category change
  const handleCategoryChange = (category: StaffCategory) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setFieldValue(`expenses.${index}.category`, category);
    // Clear staff fields when category changes
    setFieldValue(`expenses.${index}.isManualEntry`, false);
    setFieldValue(`expenses.${index}.referenceId`, "");
    setFieldValue(`expenses.${index}.paidTo`, "");
    setFieldValue(`expenses.${index}.contactNumber`, "");
    setSelectedStaff(null);
    setIsManualEntry(false);
    setSearchTerm("");
  };

  // Handle staff selection
  const handleStaffChange = (staff: any | null, inputValue?: string) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setSelectedStaff(staff);

    if (staff) {
      // Auto-fill fields from master data
      setIsManualEntry(false);
      setFieldValue(`expenses.${index}.isManualEntry`, false);
      setFieldValue(`expenses.${index}.referenceId`, staff.id);
      setFieldValue(`expenses.${index}.paidTo`, staff.name);
      setFieldValue(`expenses.${index}.contactNumber`, staff.contactNumber);
    } else if (inputValue && inputValue.trim() !== "") {
      // Manual entry mode - generate UUID and set manual entry flag
      setIsManualEntry(true);
      const newUUID = uuidv4();
      setFieldValue(`expenses.${index}.isManualEntry`, true);
      setFieldValue(`expenses.${index}.referenceId`, newUUID);
      setFieldValue(`expenses.${index}.paidTo`, inputValue.trim());
    } else {
      // Clear fields
      setIsManualEntry(false);
      setFieldValue(`expenses.${index}.isManualEntry`, false);
      setFieldValue(`expenses.${index}.referenceId`, "");
      setFieldValue(`expenses.${index}.paidTo`, "");
      setFieldValue(`expenses.${index}.contactNumber`, "");
    }
  };

  // Get option label
  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return `${option.name} - ${option.contactNumber}`;
  };

  // Render staff option
  const renderStaffOption = (props: any, option: any) => (
    <Box component="li" {...props} sx={{ fontSize: "0.75rem", py: 0.5 }}>
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
        >
          {option.name}
        </Typography>
        {option.contactNumber && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem", mt: 0.25 }}
          >
            Contact: {option.contactNumber}
          </Typography>
        )}
        {currentCategory === "DRIVER" && option.drivingLicense && (
          <Chip
            label={`License: ${option.drivingLicense}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        )}
      </Box>
    </Box>
  );

  const handleMarkAsPaid = useCallback(
    (isPaid: boolean) => {
      if (isPaid) {
        setFieldValue(`expenses.${index}.payments`, [
          ...values.expenses[index].payments!,
          {
            type: "ADVANCE",
            mode: "CASH",
            date: values.expenses[index].expenseDate || "",
            location: values.expenses[index].location || "",
            receivedFrom: values.expenses[index].paidTo || "",
            remarks: "",
            status: "PAID",
            isEdited: false,
            isDeleted: false,
            isNew: true,
            amount: values.expenses[index].amount,
            masterExpenseId: values.expenses[index].id || null,
          },
        ]);
      } else {
        setFieldValue(`expenses.${index}.payments`, []);
      }
    },
    [index, setFieldValue, values.expenses]
  );

  return (
    <Grid container item xs={12} spacing={1}>
      {!isTripExpense && (
        <>
          <Grid item xs={12} sm={2}>
            <Field
              as={TextField}
              label="Date"
              type="date"
              name={`expenses.${index}.expenseDate`}
              fullWidth
              required
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(`expenses.${index}.expenseDate`, e.target.value);
                setFieldValue(`expenses.${index}.isEdited`, true);
              }}
              {...commonDateFieldProps}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={2}>
        <Field
          as={TextField}
          select
          label="Staff Type"
          name={`expenses.${index}.category`}
          fullWidth
          required
          value={currentCategory || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleCategoryChange(e.target.value as StaffCategory)
          }
          {...commonTextFieldProps}
        >
          {STAFF_CATEGORIES.map((category) => (
            <MenuItem key={category} value={category}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {category === "DRIVER" ? (
                  <AccountCircleIcon fontSize="small" />
                ) : (
                  <CleaningServicesIcon fontSize="small" />
                )}
                {category}
              </Box>
            </MenuItem>
          ))}
        </Field>
      </Grid>

      {/* Staff Selection Autocomplete */}
      {currentCategory && (
        <Grid container item xs={12} sm={3}>
          <Grid item xs={12} sm={selectedStaff ? 10.5 : 12}>
            <Autocomplete
              freeSolo
              options={staffOptions}
              loading={isLoading}
              getOptionLabel={getOptionLabel}
              value={selectedStaff || values.expenses[index].paidTo}
              onChange={(_, newValue, reason) => {
                if (reason === "selectOption" && typeof newValue === "object") {
                  // Selected from dropdown - reset manual entry mode
                  setIsManualEntry(false);
                  setFieldValue(`expenses.${index}.isManualEntry`, false);
                  handleStaffChange(newValue);
                } else if (reason === "clear") {
                  // Clear selection - reset all fields
                  setIsManualEntry(false);
                  setFieldValue(`expenses.${index}.isManualEntry`, false);
                  handleStaffChange(null);
                }
              }}
              onInputChange={(_, inputValue, reason) => {
                setSearchTerm(inputValue);

                if (reason === "input") {
                  const matchingOption = staffOptions.find((option) =>
                    getOptionLabel(option)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  );

                  if (!matchingOption && inputValue.trim() !== "") {
                    // Manual entry - generate UUID and set manual entry flag
                    setIsManualEntry(true);
                    const newUUID = uuidv4();
                    setFieldValue(`expenses.${index}.isEdited`, true);
                    setFieldValue(`expenses.${index}.isManualEntry`, true);
                    setFieldValue(`expenses.${index}.referenceId`, newUUID);
                    setFieldValue(
                      `expenses.${index}.paidTo`,
                      inputValue.trim()
                    );
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Select ${currentCategory}`}
                  placeholder={`Search from master data or enter manually...`}
                  required
                  fullWidth
                  error={!!searchError || !!expenseErrors?.paidTo}
                  helperText={
                    searchError
                      ? searchError
                      : expenseErrors?.paidTo
                      ? "Staff name is required"
                      : `Select from dropdown or type to add new ${currentCategory.toLowerCase()}`
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  {...commonTextFieldProps}
                />
              )}
              renderOption={renderStaffOption}
              noOptionsText={
                isLoading
                  ? "Searching..."
                  : searchError
                  ? "Error loading options"
                  : `No matching ${currentCategory.toLowerCase()} found. You can still type to add manually.`
              }
            />
          </Grid>
          {selectedStaff && (
            <Grid item xs={12} sm={1.5}>
              <IconButton
                title="Show Staff Details"
                onClick={() => setShowStaffDetails(!showStaffDetails)}
              >
                <Visibility
                  fontSize="small"
                  sx={{ fontSize: "1rem" }}
                  color="primary"
                />
              </IconButton>
            </Grid>
          )}
        </Grid>
      )}

      {/* Manual Entry Alert */}
      {/* {isManualEntry && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              <strong>Manual Entry Mode:</strong> This{" "}
              {currentCategory?.toLowerCase()} is not in our master data. Please
              fill in the details manually below.
            </Typography>
          </Alert>
        </Grid>
      )} */}

      {/* Amount */}
      <Grid item xs={12} sm={2}>
        <Field name={`expenses.${index}.amount`}>
          {({ field, meta }: any) => (
            <TextField
              {...field}
              {...commonTextFieldProps}
              label="Amount"
              fullWidth
              required
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
              value={field.value ? formatInputCurrency(field.value) : ""}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, "");
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: value,
                    },
                  });
                  setFieldValue(`expenses.${index}.isEdited`, true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    component="span"
                    sx={{ mr: 0.5, fontSize: "0.75rem" }}
                  >
                    ₹
                  </Typography>
                ),
              }}
              inputProps={{
                inputMode: "decimal",
              }}
            />
          )}
        </Field>
      </Grid>

      {/* Contact Number (manual entry) */}
      {(isManualEntry ||
        (!selectedStaff && values.expenses[index]?.contactNumber)) && (
        <Grid item xs={12} sm={3}>
          <Field
            as={TextField}
            label="Contact Number"
            name={`expenses.${index}.contactNumber`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.contactNumber`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            fullWidth
            placeholder="Enter contact number (optional)"
            {...commonTextFieldProps}
            inputProps={{
              maxLength: 10,
            }}
            error={Boolean(
              expenseErrors?.contactNumber && expenseErrors?.contactNumber
            )}
            helperText={
              expenseErrors?.contactNumber && expenseErrors?.contactNumber
            }
          />
        </Grid>
      )}

      {/* Location */}
      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Location"
          name={`expenses.${index}.location`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.location`, e.target.value);
            setFieldValue(`expenses.${index}.isEdited`, true);
          }}
          fullWidth
          placeholder="Enter location"
          {...commonTextFieldProps}
        />
      </Grid>
      {/* Auto-filled Staff Details */}
      {selectedStaff && !isManualEntry && showStaffDetails && (
        <Grid item xs={12}>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              mb: 1.5,
              borderColor: "primary.light",
              bgcolor: "#F0F7FF",
            }}
          >
            <Typography variant="subtitle2" color="primary.dark" gutterBottom>
              {currentCategory} Details (From Master Data)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Name: {selectedStaff.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Contact: {selectedStaff.contactNumber}
                </Typography>
              </Grid>
              {currentCategory === "DRIVER" && selectedStaff.drivingLicense && (
                <Grid item xs={12} sm={3}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    License: {selectedStaff.drivingLicense}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => setAddNotes(!addNotes)}
          size={smallButtonProp.size}
          sx={{
            ...smallButtonProp.sx,
            color: "grey",
            borderColor: "grey.400",
            "&:hover": {
              borderColor: "grey.400",
              color: "grey",
            },
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
            {addNotes ? "Hide Notes" : "Add Notes"}
          </Typography>
        </Button>
        {Number(values.expenses[index].amount) > 0 &&
          values.expenses[index].payments?.length === 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    values.expenses[index].payments?.length! &&
                    values.expenses[index].payments?.length! > 0
                      ? true
                      : false
                  }
                  onChange={(e) => {
                    handleMarkAsPaid(e.target.checked);
                  }}
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: "0.7rem" },
                  }}
                />
              }
              label="Mark as Paid"
              sx={{
                p: 0,
                pr: 1,
                ml: 0.75,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.7rem",
                  color: "primary.main",
                },
              }}
            />
          )}
      </Grid>
      {/* Notes */}
      {addNotes && (
        <Grid item xs={12}>
          <Field
            as={TextField}
            label="Notes"
            name={`expenses.${index}.notes`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.notes`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            fullWidth
            multiline
            rows={2}
            placeholder="Any additional notes about this expense..."
            {...commonTextFieldProps}
          />
        </Grid>
      )}
    </Grid>
  );
}

export default DriverCleanerExpenseFields;
